import styled from "styled-components";
import React, { useEffect, useRef, useState } from "react";
import {
  MDBCard,
  MDBCardHeader,
  MDBCardTitle,
  MDBListGroupItem,
  MDBTooltip,
} from "mdb-react-ui-kit";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGameQuery } from "../settings/Settings.state";
import { axios } from "../lib/request";
import { Button, Row, Col } from "../App.styles";
import { Alert, FormAutocomplete, RowInfo } from "../common";
import InfiniteList from "../common/InfiniteList";
import { SoundtrackModal } from "../settings/modals";
import { useUserQuery } from "../monitor/User.state";
import { useInfiniteTeamsQuery } from "../teams/Teams.state";
import { useInfiniteChallengesQuery } from "../organize/Organize.state";
import { ReelsFormBody, ReelsList } from "./ReelsForm.style";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

const ReelRow = styled(Row)`
  margin-top: 1rem;
`;

const ReelCol = styled(Col)`
  display: block;

  p {
    margin: 1rem 0;
  }

  ${Button} {
    padding-left: 1rem;
    padding-right: 1rem;
    margin-bottom: 1rem;
  }

  br + ${Button} {
    margin-left: 0;
    margin-top: 1rem;
  }

  video {
    width: 100%;
    height: 528px;
  }
`;

export const Soundtrack = styled.audio`
  max-width: calc(100% - 24px);

  @media (min-width: ${({ theme }) => theme.minDesktopWidth}) {
    min-width: 17.5rem;
  }
`;

const ReelsForm = () => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  const [game, setGame] = useGameQuery(game_id);
  const { game: gameRole } = useUserQuery(game_id);
  const [alert, setAlert] = useState("");
  const [alertId, setAlertId] = useState(0);
  const audioRef = useRef();
  const { reel, soundtrack, task_reels, team_reels } = game;
  const { administrator, coordinator, super_admin } = gameRole;
  const [gameReelGenerated, setGameReelGenerated] = useState(false);
  const [taskReelGenerated, setTaskReelGenerated] = useState(false);
  const [teamReelGenerated, setTeamReelGenerated] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const infiniteTeamsQuery = useInfiniteTeamsQuery();
  const infiniteTasksQuery = useInfiniteChallengesQuery();

  const generateReels = (url) => {
    axios.patch(`${process.env.REACT_APP_API_GENERATE_REELS_URL}/${url}`);
    setAlert(t("Reels have been queued for this game."));
    setAlertId(alertId + 1);
  };

  useEffect(() => {
    if (isVideoPlaying) {
      localStorage.setItem("reels_playing", String(isVideoPlaying));
    } else {
      if (localStorage.getItem("reels_playing")) {
        localStorage.removeItem("reels_playing");
      }
    }
  }, [isVideoPlaying]);

  const setSoundtrack = async (soundtrack) => {
    setGame({ soundtrack });
    axios.put(`${process.env.REACT_APP_API_GAME_UPDATE_URL}/${game_id}.json`, {
      soundtrack,
    });

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.load();
    }
  };

  return (
    <>
      <Row>
        <Alert id={alertId} color="info">{alert}</Alert>
      </Row>
      <Row>
        <Col className="col--stretch">
          <MDBCard className="flex-grow-1">
            <MDBCardHeader>
              <MDBCardTitle>{t("Automated Highlight Reels")}</MDBCardTitle>
            </MDBCardHeader>
            <ReelsFormBody>
              <RowInfo help={<SoundtrackModal />} isFull rowClassName={!!soundtrack ? 'has-soundtrack' : ''}>
                <Row className="soundtrack__row">
                  <Col className="align-start col--stretch" size="12" lg={!!soundtrack ? 7 : 12}>
                    <FormAutocomplete
                      defaultValue=""
                      desc={t(
                        "Select the soundtrack you would like used for your highlight reels."
                      )}
                      name="soundtrack"
                      label={t("Highlight Reels Soundtrack")}
                      value={soundtrack}
                      setValue={setSoundtrack}
                      options={[
                        {
                          id: "",
                          name: t("No Soundtrack"),
                        },
                        {
                          id: "Scott_Holmes_-_12_-_Feeling_Sunny.m4a",
                          name: `Feeling Sunny ${t("by")} Scott Holmes`,
                        },
                        {
                          id: "John_Bartmann_-_09_-_Happy_Clappy.m4a", 
                          name: `Happy Clappy ${t("by")} John Bartmann`,
                        },
                        {
                          id: "Scott_Holmes_-_11_-_Happy_Ending.m4a",
                          name: `Happy Ending ${t("by")} Scott Holmes`,
                        },
                        {
                          id: "Scott_Holmes_-_04_-_Happy_Ukulele.m4a",
                          name: `Happy Ukulele ${t("by")} Scott Holmes`,
                        },
                        {
                          id: "Dan_Zeitune_-_Last_Dance_Instrumental.m4a",
                          name: `Last Dance ${t("by")} Dan Zeitune`,
                        },
                        {
                          id: "Scott_Holmes_-_01_-_Our_Big_Adventure.m4a",
                          name: `Our Big Adventure ${t("by")} Scott Holmes`,
                          selected: true,
                        },
                        {
                          id: "Scott_Holmes_-_07_-_Positive_and_Fun.m4a",
                          name: `Positive and Fun ${t("by")} Scott Holmes`,
                        }
                      ]}
                      stateful={false}
                      size="small"
                    />
                  </Col>
                  <Col className="align-start col--stretch" size="12" lg="5">
                    {
                        !!soundtrack 
                            ? (
                                <Soundtrack controls ref={audioRef}>
                                    <source
                                      type="audio/mp4"
                                      src={`https://ss-video-support.s3.amazonaws.com/reels/${soundtrack}`}
                                    />
                                </Soundtrack>
                              )
                            : null
                    }
                  </Col>
                </Row>
              </RowInfo>
              <ReelRow>
                <ReelCol size="12">
                  <strong>{t("Game")}</strong>
                  <p>
                    {!!reel ? (
                      <>
                        <video
                          controls
                          preload="auto"
                          onEnded={() => setIsVideoPlaying(false)}
                          onPause={() => setIsVideoPlaying(false)}
                          onPlay={() => setIsVideoPlaying(true)}
                          id="reels-video"
                          key={reel}
                        >
                          <source src={reel} type="video/mp4" />
                        </video>
                      </>
                    ) : (
                      <>
                        {t(
                          "There is no game reel available. There must be at least 3"
                        )}{" "}
                        <Link to={`/answers/monitor/${game_id}`}>
                          {t("featured")}
                        </Link>{" "}
                        {t("submissions")}.
                      </>
                    )}
                  </p>
                  {administrator || coordinator || super_admin ? (
                    <Button
                      color="success"
                      disabled={gameReelGenerated}
                      onClick={() => {
                        setGameReelGenerated(true);
                        generateReels(`${game_id}/generate_reels.json`);
                      }}
                    >
                      {t("Regenerate")}
                    </Button>
                  ) : null}
                </ReelCol>
                <ReelCol size="6">
                  <strong>{t("Teams")}</strong>
                  <ReelsList>
                    <InfiniteList
                      name="teams"
                      query={infiniteTeamsQuery}
                      noContent={null}
                      renderItem={({ ...team }) => {
                        const teamReel = team_reels.find((reel) => {
                          return reel?.name === team?.name;
                        });
                        return (
                          <MDBListGroupItem>
                            {teamReel ? (
                              <a href={teamReel?.url}>{teamReel?.name}</a>
                            ) : (
                              <>
                                <span className="no-reel">{team?.name}</span>
                                <MDBTooltip
                                  tag="span"
                                  title={t(
                                    `Team reels require teams provide a minimum of 3 media submissions.`
                                  )}
                                >
                                  <FontAwesomeIcon icon={faQuestionCircle} />
                                </MDBTooltip>
                              </>
                            )}
                          </MDBListGroupItem>
                        );
                      }}
                    />
                  </ReelsList>
                  {coordinator || administrator || super_admin ? (
                    <Button
                      color="success"
                      disabled={teamReelGenerated}
                      onClick={() => {
                        setTeamReelGenerated(true);
                        generateReels(
                          `${game_id}/generate_reels.json?reel_type=teams`
                        );
                      }}
                    >
                      {t("Regenerate")}
                    </Button>
                  ) : null}
                </ReelCol>
                <ReelCol size="6">
                  <strong>{t("Challenges")}</strong>
                  <ReelsList>
                    <InfiniteList
                      name="tasks"
                      query={infiniteTasksQuery}
                      noContent={null}
                      renderItem={({ ...task }) => {
                        const taskReel = task_reels.find((reel) => {
                          return reel?.name === task?.name;
                        });
                        return (
                          <MDBListGroupItem>
                            {taskReel ? (
                              <a href={taskReel?.url}>{taskReel?.name}</a>
                            ) : (
                              <>
                                <span className="no-reel">{task?.name}</span>
                                <MDBTooltip
                                  tag="span"
                                  title={t(
                                    `Challenge reels require a minimum of 3 teams providing submissions with attached media.`
                                  )}
                                >
                                  <FontAwesomeIcon icon={faQuestionCircle} />
                                </MDBTooltip>
                              </>
                            )}
                          </MDBListGroupItem>
                        );
                      }}
                    />
                  </ReelsList>
                  {administrator || coordinator || super_admin ? (
                    <Button
                      color="success"
                      disabled={taskReelGenerated}
                      onClick={() => {
                        setTaskReelGenerated(true);
                        generateReels(
                          `${game_id}/generate_reels.json?reel_type=challenges`
                        );
                      }}
                    >
                      {t("Regenerate")}
                    </Button>
                  ) : null}
                </ReelCol>
              </ReelRow>
            </ReelsFormBody>
          </MDBCard>
        </Col>
      </Row>
    </>
  );
};

export default ReelsForm;
